
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useReferralSystem } from "@/hooks/use-referral-system";
import { DollarSign, Calendar, AlertCircle, CheckCircle } from "lucide-react";
import { format } from "date-fns";

export const ReferralPayouts = () => {
  const { payouts, conversions, isLoadingPayouts, requestPayout } = useReferralSystem();

  if (isLoadingPayouts) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Loading Payouts...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="animate-pulse space-y-4">
              <div className="h-20 bg-gray-200 rounded"></div>
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const confirmedConversions = conversions?.filter(conv => conv.status === 'confirmed') || [];
  const availableForPayout = confirmedConversions.reduce((sum, conv) => sum + conv.commission_amount, 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'default';
      case 'processing': return 'secondary';
      case 'failed': return 'destructive';
      default: return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'processing': return <DollarSign className="h-4 w-4" />;
      case 'failed': return <AlertCircle className="h-4 w-4" />;
      default: return <Calendar className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Available for Payout
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">
              ${availableForPayout.toFixed(2)}
            </div>
            <p className="text-gray-600 mb-4">
              {confirmedConversions.length} confirmed conversions ready for payout
            </p>
            {availableForPayout > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-gray-500">
                  Minimum payout amount: $50.00
                </p>
                <Button 
                  disabled={availableForPayout < 50 || requestPayout.isPending}
                  onClick={() => {
                    // This would open a payout request modal
                    console.log('Request payout for:', confirmedConversions.map(c => c.id));
                  }}
                >
                  {requestPayout.isPending ? 'Processing...' : 'Request Payout'}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payout History</CardTitle>
        </CardHeader>
        <CardContent>
          {!payouts || payouts.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No payout requests yet.</p>
              <p className="text-sm text-gray-500 mt-2">
                Request your first payout when you have at least $50 in confirmed commissions.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {payouts.map((payout) => (
                <div key={payout.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(payout.status)}
                      <span className="font-semibold">${payout.total_amount.toFixed(2)}</span>
                    </div>
                    <Badge variant={getStatusColor(payout.status)}>
                      {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>Method: {payout.payout_method.toUpperCase()}</div>
                    {payout.payout_address && (
                      <div>Address: {payout.payout_address}</div>
                    )}
                    <div>Requested: {format(new Date(payout.requested_at), 'MMM dd, yyyy')}</div>
                    {payout.processed_at && (
                      <div>Processed: {format(new Date(payout.processed_at), 'MMM dd, yyyy')}</div>
                    )}
                    {payout.transaction_hash && (
                      <div className="font-mono text-xs">
                        TX: {payout.transaction_hash.substring(0, 20)}...
                      </div>
                    )}
                    {payout.error_message && (
                      <div className="text-red-600">Error: {payout.error_message}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
